import React, { useEffect, useState, useMemo } from "react";
import { MapContainer, TileLayer, Marker, Polyline, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import api from "@/lib/api";
import { Loader2, MapPin } from "lucide-react";

// Auto-ajustar el mapa para que muestre todos los puntos
function AutoFit({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return;
    if (points.length === 1) {
      map.setView([points[0].lat, points[0].lng], 13);
      return;
    }
    const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
    map.fitBounds(bounds, { padding: [30, 30], maxZoom: 14 });
  }, [points, map]);
  return null;
}

// Marcador numerado tipo "pin con #"
function numeroIcon(num, opts = {}) {
  const color = opts.color || "#f59e0b";
  const size = opts.size || 30;
  return L.divIcon({
    className: "ruta-marker",
    html: `<div style="
      position:relative;width:${size}px;height:${size + 8}px;
    ">
      <div style="
        position:absolute;top:0;left:0;
        width:${size}px;height:${size}px;border-radius:50% 50% 50% 0;
        transform:rotate(-45deg);
        background:${color};
        border:2px solid #fff;
        box-shadow:0 2px 6px rgba(0,0,0,0.4);
      "></div>
      <div style="
        position:absolute;top:5px;left:0;width:${size}px;height:${size - 10}px;
        display:flex;align-items:center;justify-content:center;
        font-size:${size < 26 ? 11 : 13}px;font-weight:700;color:#1f1f1f;
        text-shadow:0 1px 0 rgba(255,255,255,0.5);
      ">${num}</div>
    </div>`,
    iconSize: [size, size + 8],
    iconAnchor: [size / 2, size + 8],
    popupAnchor: [0, -(size + 4)],
  });
}

/**
 * Mapa de ruta — muestra los puntos numerados (#1, #2…) y la geometría real (carretera).
 *
 * Props:
 *   rutaId: ID de la ruta (obligatorio)
 *   orden: orden personalizado de linea_ids (opcional, default = orden actual)
 *   height: alto del mapa en CSS (default 320)
 *   conGeometry: si true (default) pide la ruta real (consume cuota ORS)
 *   refreshKey: cambia este valor para forzar refetch
 */
export default function RutaMapa({ rutaId, orden, height = 320, conGeometry = true, refreshKey = 0, className = "" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!rutaId) return;
    let cancel = false;
    setLoading(true);
    setError(null);
    api.post(`/rutas/${rutaId}/mapa`, { orden, incluir_geometry: conGeometry })
      .then((r) => { if (!cancel) setData(r.data); })
      .catch((e) => {
        if (!cancel) setError(e?.response?.data?.detail || "No se pudo cargar el mapa");
      })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [rutaId, JSON.stringify(orden), conGeometry, refreshKey]);

  const puntos = useMemo(() => (data?.puntos || []).filter((p) => !p.sin_coords), [data]);
  // GeoJSON LineString viene en [lng,lat]; Leaflet Polyline espera [lat,lng]
  const polylinePositions = useMemo(() => {
    if (!data?.geometry?.coordinates) return null;
    return data.geometry.coordinates.map(([lng, lat]) => [lat, lng]);
  }, [data]);

  if (loading) {
    return (
      <div className={`bg-zinc-950/40 border border-zinc-800 rounded-lg flex items-center justify-center ${className}`}
        style={{ height }}>
        <div className="text-zinc-500 text-sm flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Cargando mapa…
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className={`bg-red-500/10 border border-red-500/30 rounded-lg flex items-center justify-center px-4 ${className}`}
        style={{ height }}>
        <div className="text-red-300 text-xs">⚠️ {error}</div>
      </div>
    );
  }
  if (puntos.length === 0) {
    return (
      <div className={`bg-zinc-950/40 border border-zinc-800 rounded-lg flex items-center justify-center ${className}`}
        style={{ height }}>
        <div className="text-zinc-500 text-xs flex items-center gap-2">
          <MapPin className="w-4 h-4" /> Sin puntos con coordenadas para mostrar
        </div>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} data-testid="ruta-mapa">
      {data?.geometry_meta && (
        <div className="absolute top-2 right-2 z-[400] bg-zinc-950/85 backdrop-blur border border-zinc-700 rounded px-2 py-1 text-[10px] text-zinc-200 shadow-lg">
          🛣️ {data.geometry_meta.distance_km} km · {Math.floor(data.geometry_meta.duration_min / 60)}h{Math.round(data.geometry_meta.duration_min % 60).toString().padStart(2, "0")} cond.
        </div>
      )}
      {data?.puntos_sin_coords > 0 && (
        <div className="absolute top-2 left-2 z-[400] bg-amber-500/20 border border-amber-500/40 rounded px-2 py-1 text-[10px] text-amber-200 shadow">
          ⚠️ {data.puntos_sin_coords} parada{data.puntos_sin_coords !== 1 ? "s" : ""} sin geocodificar
        </div>
      )}
      <MapContainer
        center={[puntos[0].lat, puntos[0].lng]}
        zoom={12}
        style={{ height, width: "100%", borderRadius: "0.5rem", zIndex: 0 }}
        scrollWheelZoom={false}
      >
        <TileLayer
          attribution='&copy; OpenStreetMap'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <AutoFit points={puntos} />
        {polylinePositions && (
          <Polyline
            positions={polylinePositions}
            pathOptions={{ color: "#f59e0b", weight: 4, opacity: 0.85, lineCap: "round", lineJoin: "round" }}
          />
        )}
        {puntos.map((p) => (
          <Marker key={p.linea_id} position={[p.lat, p.lng]} icon={numeroIcon(p.orden)}>
            <Popup>
              <div style={{ fontFamily: "system-ui", fontSize: 12 }}>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>#{p.orden} {p.nombre}</div>
                {p.ciudad && <div style={{ color: "#666" }}>{p.ciudad}</div>}
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}
